import {
  alpha,
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  Popover,
  Tooltip,
  Typography
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import NotificationsActiveTwoToneIcon from '@mui/icons-material/NotificationsActiveTwoTone';
import { styled } from '@mui/material/styles';

import { formatDistance, subDays } from 'date-fns';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { useNavigate } from 'react-router';
import { Bell } from 'lucide-react';
import { resolveAdminNotificationRoute } from './notificationRoutes';
import axios from 'axios';
import { useAdminWorkspace } from 'src/workspaces/AdminWorkspaceProvider';

const NotificationsBadge = styled(Badge)(
  ({ theme }) => `
    
    .MuiBadge-badge {
        background-color: ${alpha(theme.palette.error.main, 0.1)};
        color: ${theme.palette.error.main};
        min-width: 16px; 
        height: 16px;
        padding: 0;

        &::after {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            border-radius: 50%;
            box-shadow: 0 0 0 1px ${alpha(theme.palette.error.main, 0.3)};
            content: "";
        }
    }
`
);

function HeaderNotifications() {
  const navigate = useNavigate();
  const workspace = useAdminWorkspace();
  const companyId = workspace.kind === 'company' && workspace.status === 'ready'
    ? workspace.workspace.company.id : undefined;
  const actorId = workspace.kind === 'company' || workspace.kind === 'platform'
    ? workspace.principal.id : undefined;
  const inboxBase = companyId ? `/companies/${companyId}/notifications` : '/notifications/admin';

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // const pageSize = 20;

  // **Infinite Query for Notifications**
  const {
    data,
    error,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch
  }: any = useInfiniteQuery(
    ['notifications', actorId, companyId ?? 'platform'],
    ({ pageParam = 1 }) => axios.get(inboxBase, { params: { page: pageParam, pageSize } }),
    {
      enabled: Boolean(actorId) && (workspace.kind === 'platform' || Boolean(companyId)),
      getNextPageParam: (lastPage, allPages) => {
        const totalPages = lastPage?.data?.meta?.totalPages ?? 1;
        return allPages.length < totalPages ? allPages.length + 1 : undefined;
      },
      keepPreviousData: false
    }
  );

  const notifications = data?.pages?.flatMap((page) => page?.data?.data) ?? [];
  const unReadCount = data?.pages?.[0]?.data?.meta?.totalUnReadCount ?? 0;

  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const listRef = useRef<HTMLUListElement>(null);

  const handleOpen = (): void => {
    setOpen(true);
    refetch();
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleOnClickAction = async (item) => {
    setActionError(null);
    try {
    if (!item?.isRead) await axios.post(`${inboxBase}/${item.id}/read`);

    // Registry-driven routing (epic #946, P2). Replaces the hardcoded if-chain
    // that navigated only a few types; the resolver now also routes
    // assignment/move decisions, quick-accepted bids, and shift-change
    // requests, which carry an eventId but previously did nothing on click.
    const route = resolveAdminNotificationRoute(item, companyId);
    if (route) {
      navigate(route.path, route.state ? { state: route.state } : undefined);
    }

    handleClose();
    refetch();
    } catch {
      setActionError('Could not update this notification. Please try again.');
    }
  };

  const MarkAllAsRead = async () => {
    try {
      setActionError(null);
      await axios.post(`${inboxBase}/read-all`);

      refetch(); // Refresh the list after marking all as read
    } catch (err) {
      setActionError('Could not mark notifications as read. Please try again.');
    }
  };

  // **Scroll Event for Infinite Scroll**
  const handleScroll = () => {
    if (!listRef.current || isFetchingNextPage || !hasNextPage) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 5) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    const listElement = listRef.current;
    if (listElement) {
      listElement.addEventListener('scroll', handleScroll);
      return () => listElement.removeEventListener('scroll', handleScroll);
    }
  }, [isFetchingNextPage, hasNextPage]);

  return (
    <>
      <Tooltip arrow title="Notifications">
        <button
          ref={ref}
          className="relative inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03] text-white/[0.72] transition-all duration-200 hover:bg-white/[0.08] hover:text-white"
          onClick={handleOpen}
          type="button"
          aria-label="Notifications"
        >
          <Bell size={16} />

          {/* Badge */}
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1
                   flex items-center justify-center
                   text-[10px] font-medium
                   bg-[#ff4d6d] text-white/90 rounded-full
                   shadow-sm"
          >
            {unReadCount}
          </span>
        </button>
      </Tooltip>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant="h5">Notifications</Typography>
          {unReadCount > 0 && (
            <Button
              size="small"
              onClick={MarkAllAsRead}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                color: 'primary.main'
              }}
            >
              Mark All Read
            </Button>
          )}
        </Box>
        <Divider />
        {(error || actionError) && <Alert severity="error" action={error ? <Button onClick={() => refetch()}>Retry</Button> : undefined}>
          {actionError || 'Notifications could not be loaded.'}
        </Alert>}
        {isLoading && <Box sx={{ p: 2 }}><CircularProgress size={20} aria-label="Loading notifications" /></Box>}
        {!isLoading && !error && notifications.length === 0 && <Typography sx={{ p: 2 }}>No notifications.</Typography>}
        <List
          ref={listRef}
          onScroll={handleScroll}
          sx={{ p: 0, maxHeight: '500px', overflowY: 'auto' }}
        >
          {notifications.map((item: any) => (
            <ListItem
              key={item.id}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleOnClickAction(item);
                }
              }}
              sx={{
                p: 2,
                minWidth: 350,
                display: { xs: 'block', sm: 'flex' },
                borderBottom: '1px solid #e5e5e5',
                cursor: 'pointer',
                backgroundColor:
                  item.isRead === false ? '#F2F5F9' : 'transparent', // light background for unread
                '&:hover': {
                  backgroundColor: '#F2F5F9' // hover effect (optional)
                }
              }}
              onClick={() => handleOnClickAction(item)}
            >
              <Box flex="1">
                <Box display="flex" justifyContent="space-between">
                  <Typography
                    sx={{
                      fontWeight: 'bold',
                      display: 'flex',
                      // alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    {item.isRead === false && (
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          backgroundColor: '#1976d2', // MUI primary blue
                          mt: '6px',
                          // mr: 2,
                          flexShrink: 0
                        }}
                      />
                    )}{' '}
                    {item?.title}
                  </Typography>
                  <Typography variant="caption" sx={{ textTransform: 'none' }}>
                    {moment(item?.createdAt).fromNow()}
                  </Typography>
                </Box>
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                >
                  {item?.data}
                </Typography>
              </Box>
            </ListItem>
          ))}
          {isFetchingNextPage && (
            <Box textAlign="center" p={1}>
              <CircularProgress size={14} />
            </Box>
          )}
        </List>
      </Popover>
    </>
  );
}

export default HeaderNotifications;
